export interface PropertyOwnerReadDefinition {
    read(source: object, key: string): unknown;
    bind(source: object): Record<string, unknown>;
}

type Field<Source> = {
    read(source: Source): unknown;
    write?(source: Source, value: unknown): void;
};

const bindings = new WeakMap<object, { source: object; definition: PropertyOwnerReadDefinition }>();

/** Separates a synthetic row's operations from its current source instance. */
export class PropertyOwnerDefinition<Source extends object> implements PropertyOwnerReadDefinition {
    constructor(private readonly fields: Readonly<Record<string, Field<Source>>>) {}

    read(source: Source, key: string): unknown {
        const field = this.fields[key];
        if (!field) throw new Error(`Unknown synthetic property '${key}'`);
        return field.read(source);
    }

    bind(source: Source): Record<string, unknown> {
        const owner = Object.create(null) as Record<string, unknown>;
        for (const [key, field] of Object.entries(this.fields)) {
            Object.defineProperty(owner, key, {
                enumerable: true, configurable: true,
                get: () => field.read(source),
                set: field.write ? value => field.write!(source, value) : undefined,
            });
        }
        bindings.set(owner, { source, definition: this });
        return owner;
    }
}

export function propertyOwnerBinding(owner: object) {
    return bindings.get(owner);
}
